<ng-template #modalTemplate let-modal>
  <div class="modal-header bg-primary text-white py-3">
    <div class="d-flex align-items-center gap-2 flex-grow-1">
      <span class="ic-pulse">
        <i class="ri-phone-fill fs-4"></i>
      </span>
      <div>
        <div class="fw-semibold">{{ "label-title.incoming-call" | translate }}</div>
        <div class="small opacity-75">{{ callerDisplay() }}</div>
      </div>
    </div>
    <button type="button" class="btn-close btn-close-white" aria-label="Close" (click)="close()"></button>
  </div>

  <div class="modal-body">
    @switch (snapshot.state) {
      @case ("searching-customer") {
        <div class="text-center py-4">
          <span class="spinner-border text-primary mb-3"></span>
          <div class="fw-semibold">{{ "label-title.searching-customer" | translate }}</div>
          <div class="small text-muted">{{ callerDisplay() }}</div>
        </div>
      }
      @case ("customer-found") {
        <div class="text-uppercase small text-muted fw-semibold mb-2">{{ "label-title.customer" | translate }}</div>
        <div class="d-flex align-items-center gap-3 ic-result-card">
          <div class="ic-avatar bg-primary text-white">
            {{ (snapshot.customer.firstName?.[0] || "") + (snapshot.customer.lastName?.[0] || "") | uppercase }}
          </div>
          <div class="flex-grow-1 min-w-0">
            <div class="fw-semibold text-truncate">
              {{ snapshot.customer.firstName }} {{ snapshot.customer.lastName }}
            </div>
            <div class="small text-muted text-truncate">
              <i class="ri-phone-line me-1"></i>{{ snapshot.customer.countryPhoneCode }} {{ snapshot.customer.phone }}
            </div>
            @if (snapshot.customer.email) {
              <div class="small text-muted text-truncate">
                <i class="ri-mail-line me-1"></i>{{ snapshot.customer.email }}
              </div>
            }
          </div>
        </div>

        @if (snapshot.activeBooking) {
          <section class="ic-current" id="caller-current-trip">
            <div class="ic-current-head">
              <b>Current trip</b>
              <span>#{{ snapshot.activeBooking.uniqueId }}</span>
              @if (statusKey(snapshot.activeBooking.status)) {
                <span>{{ statusKey(snapshot.activeBooking.status) | translate }}</span>
              }
            </div>
            <p class="ic-route">{{ tripRoute(snapshot.activeBooking) }}</p>
            <p id="caller-live">{{ currentLive || "No live driver position" }}</p>
            <p id="caller-eta">{{ currentEta || "ETA unavailable" }}</p>
            <app-trip-vehicle-track
              [booking]="snapshot.activeBooking"
              [bookingId]="snapshot.activeBooking._id"
              [compact]="true"
              [live]="true">
            </app-trip-vehicle-track>
          </section>
        }

        @if (snapshot.recentTrips?.length) {
          <section class="ic-trips" id="caller-recent-trips">
            <div class="ic-kicker">Recent trips</div>
            @for (trip of snapshot.recentTrips; track trip._id || trip.uniqueId) {
              <div class="ic-trip">
                <div>
                  <b>#{{ trip.uniqueId }}</b>
                  <span>{{ tripRoute(trip) }}</span>
                </div>
                <button type="button" (click)="openTrip(trip, 8)">Vehicle Track</button>
                <button type="button" (click)="openTrip(trip, 7)">Logs</button>
              </div>
            }
          </section>
        }

        @if (snapshot.driver) {
          <hr class="my-3" />
          <div class="text-uppercase small text-muted fw-semibold mb-2">
            <i class="ri-car-line me-1"></i>{{ "label-title.also-a-driver" | translate }}
          </div>
          <div class="d-flex align-items-center gap-3 ic-result-card">
            <div class="ic-avatar bg-success text-white">
              {{ (snapshot.driver.firstName?.[0] || "") + (snapshot.driver.lastName?.[0] || "") | uppercase }}
            </div>
            <div class="flex-grow-1 min-w-0">
              <div class="fw-semibold text-truncate">
                {{ snapshot.driver.firstName }} {{ snapshot.driver.lastName }}
              </div>
              <div class="small mt-1">
                <span class="badge" [class]="driverStatusBadgeClass(snapshot.driver.status)">
                  {{ driverStatusLabel(snapshot.driver.status) | translate }}
                </span>
              </div>
            </div>
          </div>
        }
      }
      @case ("searching-driver") {
        <div class="text-center py-4">
          <span class="spinner-border text-success mb-3"></span>
          <div class="fw-semibold">{{ "label-title.searching-driver" | translate }}</div>
          <div class="small text-muted">{{ callerDisplay() }}</div>
        </div>
      }
      @case ("driver-found") {
        <div class="text-uppercase small text-muted fw-semibold mb-2">{{ "label-title.driver" | translate }}</div>
        <div class="d-flex align-items-center gap-3 ic-result-card">
          <div class="ic-avatar bg-success text-white">
            {{ (snapshot.driver.firstName?.[0] || "") + (snapshot.driver.lastName?.[0] || "") | uppercase }}
          </div>
          <div class="flex-grow-1 min-w-0">
            <div class="fw-semibold text-truncate">
              {{ snapshot.driver.firstName }} {{ snapshot.driver.lastName }}
            </div>
            <div class="small text-muted text-truncate">
              <i class="ri-phone-line me-1"></i>{{ snapshot.driver.countryPhoneCode }} {{ snapshot.driver.phone }}
            </div>
            @if (snapshot.driver.email) {
              <div class="small text-muted text-truncate">
                <i class="ri-mail-line me-1"></i>{{ snapshot.driver.email }}
              </div>
            }
            <div class="small mt-1">
              <span class="badge" [class]="driverStatusBadgeClass(snapshot.driver.status)">
                {{ driverStatusLabel(snapshot.driver.status) | translate }}
              </span>
            </div>
          </div>
        </div>
      }
      @case ("not-found") {
        <div class="text-center py-4">
          <i class="ri-user-add-line display-5 text-muted d-block mb-2"></i>
          <div class="fw-semibold">{{ "label-title.no-match-found" | translate }}</div>
          <div class="small text-muted">{{ "label-title.no-match-hint" | translate }}</div>
        </div>
      }
    }
  </div>

  <div class="modal-footer d-flex flex-wrap gap-2 justify-content-between">
    <div class="d-flex gap-2 flex-wrap">
      @if (snapshot.state === "customer-found" || snapshot.state === "not-found") {
        <button type="button" class="btn btn-sm btn-outline-success" (click)="searchAsDriver()">
          <i class="ri-car-line me-1"></i>{{ "button-title.search-as-driver" | translate }}
        </button>
      }
      @if (snapshot.state === "driver-found") {
        <button type="button" class="btn btn-sm btn-outline-primary" (click)="searchAsCustomer()">
          <i class="ri-user-line me-1"></i>{{ "button-title.search-as-customer" | translate }}
        </button>
      }
    </div>
    <div class="d-flex gap-2 flex-wrap justify-content-end">
      <button type="button" class="btn btn-sm btn-light" (click)="close()">
        {{ "button-title.dismiss" | translate }}
      </button>
      @if (snapshot.state === "customer-found") {
        <button type="button" class="btn btn-sm btn-primary" (click)="openQuickBooking()">
          <i class="ri-arrow-right-line me-1"></i>{{ "button-title.open-in-quick-booking" | translate }}
        </button>
      }
      @if (snapshot.state === "driver-found") {
        <button type="button" class="btn btn-sm btn-success" (click)="openDriverProfile()">
          <i class="ri-arrow-right-line me-1"></i>{{ "button-title.open-driver-profile" | translate }}
        </button>
      }
      @if (snapshot.state === "not-found") {
        <button type="button" class="btn btn-sm btn-primary" (click)="openQuickBooking()">
          <i class="ri-user-add-line me-1"></i>{{ "button-title.use-as-new" | translate }}
        </button>
      }
    </div>
  </div>
</ng-template>
<app-booking-details-modal #callerBookingDetails></app-booking-details-modal>
